"use client";
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const LIME = '#ceff1a';
const PURPLE = '#8b5cf6';

const items = [
    {
        title: 'Landing Pages',
        desc: 'Improve messaging, structure, relevance, trust signals and calls-to-action based on the audience and traffic source.',
        icon: (
            <svg width="30" height="30" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="5" width="24" height="22" rx="3" stroke="currentColor" strokeWidth="2" />
                <path d="M4 11h24" stroke="currentColor" strokeWidth="2" />
                <path d="M9 17h14M9 21h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        title: 'User Journeys',
        desc: 'Identify friction between the first interaction and the action you want visitors to take.',
        icon: (
            <svg width="30" height="30" viewBox="0 0 32 32" fill="none">
                <circle cx="6" cy="8" r="3" stroke="currentColor" strokeWidth="2" />
                <circle cx="26" cy="24" r="3" stroke="currentColor" strokeWidth="2" />
                <path d="M8.5 9.5c4 3 6 6 6 10s3 6 8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="1 4" />
            </svg>
        ),
    },
    {
        title: 'Messaging & Value Proposition',
        desc: 'Make it easier for visitors to understand what you offer, why it matters and why they should choose you.',
        icon: (
            <svg width="30" height="30" viewBox="0 0 32 32" fill="none">
                <path d="M4 8a3 3 0 0 1 3-3h18a3 3 0 0 1 3 3v11a3 3 0 0 1-3 3H12l-6 6v-6H7a3 3 0 0 1-3-3V8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Forms & Conversion Paths',
        desc: 'Reduce unnecessary friction and make important actions easier to complete.',
        icon: (
            <svg width="30" height="30" viewBox="0 0 32 32" fill="none">
                <rect x="5" y="4" width="22" height="24" rx="3" stroke="currentColor" strokeWidth="2" />
                <rect x="9" y="9" width="14" height="4" rx="1" stroke="currentColor" strokeWidth="2" />
                <rect x="9" y="17" width="14" height="4" rx="1" stroke="currentColor" strokeWidth="2" />
            </svg>
        ),
    },
    {
        title: 'Calls-to-Action',
        desc: 'Improve the clarity, placement and context of key actions throughout the customer journey.',
        icon: (
            <svg width="30" height="30" viewBox="0 0 32 32" fill="none">
                <rect x="5" y="12" width="22" height="9" rx="4.5" stroke="currentColor" strokeWidth="2" />
                <path d="M22 16.5h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        title: 'A/B Testing',
        desc: 'Test meaningful hypotheses rather than making large website changes based purely on assumptions.',
        icon: (
            <svg width="30" height="30" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="6" width="10" height="20" rx="2" stroke="currentColor" strokeWidth="2" />
                <rect x="18" y="6" width="10" height="20" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M9 12v4M23 16v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
    },
];

function ManageRow({ item, index, active, onActivate }: { item: typeof items[0]; index: number; active: boolean; onActivate: () => void }) {
    const num = String(index + 1).padStart(2, '0');
    return (
        <div
            onMouseEnter={onActivate}
            className="grid grid-cols-1 md:grid-cols-[minmax(0,340px)_160px_minmax(0,1fr)] items-center gap-3 md:gap-8 py-6 md:py-7 cursor-default"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
        >
            <div className="flex items-baseline gap-4">
                <span
                    className="text-xl font-bold shrink-0 transition-colors duration-300"
                    style={{ fontFamily: CLASH, color: active ? '#ffffff' : 'rgba(255,255,255,0.25)' }}
                >
                    {num}
                </span>
                <h3
                    className="text-xl md:text-2xl font-bold leading-snug transition-colors duration-300"
                    style={{ fontFamily: CLASH, color: active ? '#ffffff' : 'rgba(255,255,255,0.35)' }}
                >
                    {item.title}
                </h3>
            </div>

            <div className="hidden md:flex items-center justify-center">
                <AnimatePresence>
                    {active && (
                        <motion.div
                            key="icon"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            className="w-28 h-28 rounded-2xl flex items-center justify-center overflow-hidden"
                            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: LIME }}
                        >
                            <motion.div
                                animate={{ y: [0, -6, 0] }}
                                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                            >
                                {React.cloneElement(item.icon, { width: 40, height: 40 })}
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <p
                className="text-sm md:text-base leading-relaxed transition-colors duration-300"
                style={{ fontFamily: MANROPE, color: active ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.28)' }}
            >
                {item.desc}
            </p>
        </div>
    );
}

export default function CROWhatWeManage() {
    const [active, setActive] = useState(0);

    return (
        <section className="relative overflow-hidden pt-20 pb-20 md:pt-28 md:pb-28 px-6 md:px-16 lg:px-24" style={{ background: '#080400' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, rgba(165,180,252,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1200px] mx-auto flex flex-col gap-12 relative z-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div className="flex flex-col gap-4">
                        <motion.span
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="text-xs font-bold tracking-[0.2em] uppercase"
                            style={{ color: LIME, fontFamily: CLASH }}
                        >
                            What We Cover
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                            style={{ fontFamily: CLASH, color: '#fbfdfd' }}
                        >
                            What We Optimise
                        </motion.h2>
                    </div>
                    <span className="text-sm font-mono" style={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.05em' }}>
                        6 areas we cover
                    </span>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="flex flex-col"
                    style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}
                    onMouseLeave={() => setActive(0)}
                >
                    {items.map((it, i) => (
                        <ManageRow key={it.title} item={it} index={i} active={active === i} onActivate={() => setActive(i)} />
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
